<script
  setup
  lang="ts"
  generic="
    Source extends OptionsSource = OptionsSource,
    ValueKey extends string = 'id',
    Multiple extends boolean = false,
    AsObject extends boolean = false,
    Clean extends boolean = false
  "
>
import type { InputMessageValue } from '@ui/components/input/InputMessageTypes'
import type { BackgroundType, MessageVariety, SizesType } from '@ui/styles'
import type { SelectionEvents } from './composables/useValue'
import type {
  OptionsSource,
  SelectExternalItem,
  SelectItem,
  SelectItems,
  SelectModelValue,
  SelectOption,
  SelectOptionValue,
  SelectOutput,
} from './SelectTypes'
import { BottomSheetSnapPoints } from '@ui/components/bottom-sheet/constants'
import Button from '@ui/components/button/Button.vue'
import ChevronIconBig from '@ui/components/chevron/ChevronIconBig.vue'
import Circle from '@ui/components/loader/Circle.vue'
import { ButtonVariant, Sizes } from '@ui/styles'
import { Placements } from '@ui/utils/placements'
import { useElementSize } from '@vueuse/core'
import { computed, ref, shallowRef, watch } from 'vue'
import DropdownAdaptive from '../../dropdown/DropdownAdaptive.vue'
import { useDropdown } from '../../dropdown/useDropdown'
import List from '../../list/List.vue'
import ListItem from '../../list/ListItem.vue'
import Input from '../Input.vue'
import { focusOpenOption } from './composables/focusOpenOption'
import useOptions from './composables/useOptions'
import useSearch from './composables/useSearch'
import useValue from './composables/useValue'
import { externalOptionValue, sameOptionValue } from './SelectTypes'

type SnapPoint = number | `${number}%`
type ExternalItem = SelectExternalItem<Source, ValueKey, AsObject>
type ModelOutput = SelectOutput<Source, ValueKey, Multiple, AsObject, Clean>

// Explicit intersections keep Vue's runtime Boolean/String prop casting;
// the SFC compiler cannot infer runtime types from generic constraints alone.
const props = withDefaults(
  defineProps<{
    modelValue?: SelectModelValue
    size?: SizesType
    label?: string
    placeholder?: string
    tooltip?: string
    message?: InputMessageValue
    variety?: MessageVariety
    clean?: Clean & boolean
    disabled?: boolean
    pending?: boolean
    readonly?: boolean
    background?: BackgroundType
    max?: number
    multiple?: Multiple & boolean
    labelProp?: string
    valueProp?: ValueKey & string
    trackProp?: string
    valueAsObject?: AsObject & boolean
    searchable?: boolean
    labelTextFormatter?: (value: unknown, getDefaultLabelText: (value: unknown) => string) => string
    options?: Source
    maxWidth?: string
    bottomSheetLabel?: string
    bottomSheetSnapPoints?: ReadonlyArray<SnapPoint>
    chevron?: boolean
  }>(),
  {
    size: Sizes.Luke,
    label: '',
    placeholder: '',
    tooltip: '',
    message: '',
    clean: () => false as Clean,
    disabled: false,
    pending: false,
    readonly: false,
    max: -1,
    multiple: () => false as Multiple,
    labelProp: 'label',
    valueProp: () => 'id' as ValueKey,
    trackProp: 'label',
    valueAsObject: () => false as AsObject,
    searchable: false,
    bottomSheetLabel: '',
    chevron: true,
  },
)

const emit = defineEmits<{
  'clear': []
  'update:modelValue': [value: ModelOutput]
  'input:value': [value: SelectItems]
  'select': [value: ExternalItem, item: SelectItem<Source>]
  'deselect': [value: ExternalItem, item: SelectItem<Source>]
  'input': [value: SelectModelValue]
  'search': [value: string]
  'paste': [event: ClipboardEvent]
}>()

defineSlots<{
  empty?: (props: { close: () => void }) => void
  before?: (props: { close: () => void; search: string; updateSearch: (value: string) => void }) => void
  after?: (props: { close: () => void }) => void
  item?: (props: { value: SelectOptionValue<Source, ValueKey>; label: string; item: SelectItem<Source> }) => void
}>()

const { show, placement, openStatus, toggle } = useDropdown()
const input = ref<InstanceType<typeof Input>>()
const dropdown = ref<InstanceType<typeof DropdownAdaptive>>()
const list = ref<HTMLElement | { $el?: HTMLElement }>()
const reference = computed(() => input.value?.container.border)
const { width } = useElementSize(reference)

// The runtime representation is shared across modes. Only this boundary maps
// it to the model and item types inferred from options and the mode props.
const events: SelectionEvents = {
  updateModel(value) {
    emit('update:modelValue', value as ModelOutput)
  },
  inputValue: value => emit('input:value', value),
  clear: () => emit('clear'),
  select: option =>
    emit('select', externalOptionValue(option, props.valueAsObject) as ExternalItem, option.item as SelectItem<Source>),
  deselect: option =>
    emit(
      'deselect',
      externalOptionValue(option, props.valueAsObject) as ExternalItem,
      option.item as SelectItem<Source>,
    ),
}
const { committed, setInternal, update, clear: clearValue, hasSelected, labelText } = useValue(props, events)
const draft = shallowRef<SelectOption[]>()
const deferredSelection = computed(() => props.multiple && show.value && Boolean(dropdown.value?.isMobile))

const { search, clearSearch, searchFilter } = useSearch()
const { filteredOptions, findOption, isSelected, handleOptionClick, invalidateModel } = useOptions(props, events, {
  committed,
  draft,
  update,
  search,
  searchFilter,
  clearSearch,
  setInternal,
  show,
})

watch(
  [deferredSelection, committed],
  ([deferred, value], [wasDeferred, previous]) => {
    if (!deferred) {
      draft.value = undefined
      return
    }
    const sameSelection =
      value.length === previous.length
      && value.every((option, index) => sameOptionValue(option.value, previous[index]!.value))
    if (wasDeferred && sameSelection && draft.value !== undefined) {
      // Refresh metadata while retaining every local addition and removal.
      draft.value = draft.value.map(option => findOption(option.value) ?? option)
    } else {
      draft.value = [...value]
    }
  },
  { flush: 'sync' },
)

const selectedValues = computed(() => {
  const values = filteredOptions.value.filter(isSelected).map(option => option.value)
  return props.multiple ? values : values[0]
})

function clear() {
  if (props.clean) {
    invalidateModel()
    clearValue()
  }
}

function applySelection() {
  if (!draft.value) {
    return
  }
  const previous = committed.value
  const next = draft.value
  invalidateModel()
  update(next)
  for (const option of previous) {
    if (!next.some(value => sameOptionValue(value.value, option.value))) {
      events.deselect(option)
    }
  }
  for (const option of next) {
    if (!previous.some(value => sameOptionValue(value.value, option.value))) {
      events.select(option)
    }
  }
  closeDropdown()
}

const chevronRef = ref<InstanceType<typeof ChevronIconBig>>()
const ignore = [chevronRef, reference]
const openable = computed(() => !props.disabled && !props.readonly && !props.pending)

watch(
  () => props.pending,
  pending => {
    if (pending) {
      show.value = false
    }
  },
)

function getOptionKey(option: SelectOption, index: number) {
  return `${typeof option.value}:${String(option.value)}:${index}`
}

function slotValue(option: SelectOption) {
  return option.value as SelectOptionValue<Source, ValueKey>
}

function slotItem(option: SelectOption) {
  return option.item as SelectItem<Source>
}

const focusTargetOption = computed(
  () =>
    filteredOptions.value.find(option => isSelected(option) && !option.disabled)
    ?? filteredOptions.value.find(option => !option.disabled),
)

watch(show, open => {
  if (!open) {
    clearSearch()
    return
  }
  focusOpenOption(list)
})

function onToggle() {
  if (openable.value) {
    toggle()
  }
}

function closeDropdown() {
  show.value = false
}
</script>

<template lang="pug">
Input(
  ref="input"
  :modelValue="show ? search : labelText"
  :size="size"
  :label="label"
  :clean="clean"
  :tooltip="tooltip"
  :readonly="readonly"
  :disabled="disabled || pending"
  :aria-busy="pending || undefined"
  :placeholder="hasSelected ? labelText : placeholder"
  :variety="variety"
  :message="message"
  :background="background"
  :open="openStatus"
  @update:modelValue="search = String($event)"
  @click:input="onToggle"
  @clear="clear"
)
  template(v-if="pending || (chevron && !disabled)" #left)
    Circle.icon.flex-shrink-0(v-if="pending" aria-hidden="true")
    ChevronIconBig.icon(v-else ref="chevronRef" :show="show" :class="{ 'cursor-pointer': openable }")
  template(v-if="!searchable" #input)
    span.w-100p.text-ellipsis
      template(v-if="hasSelected") {{ labelText ? $t(labelText) : '' }}
      .placeholder(v-else) {{ placeholder ? $t(placeholder) : '' }}
  template(#additional)
    DropdownAdaptive(
      ref="dropdown"
      v-model:show="show"
      v-model:placement="placement"
      :offset="2"
      :label="bottomSheetLabel || placeholder || label"
      :reference="reference"
      :width="maxWidth || `${width}px`"
      :divider="multiple"
      :placements="[Placements.BottomStart, Placements.TopStart, Placements.TopEnd, Placements.BottomEnd]"
      :ignore="ignore"
      :snapPoints="bottomSheetSnapPoints || (searchable ? BottomSheetSnapPoints.Search : undefined)"
    )
      template(#default)
        List(ref="list")
          slot(name="before" :close="closeDropdown" :search="search" :updateSearch="v => search = v")
          template(v-if="!filteredOptions.length")
            slot(name="empty" :close="closeDropdown")
              ListItem(:label="search ? 'general.nothing_found' : ''" :disabled="true")
          ListItem(
            v-for="(option, index) of filteredOptions"
            :key="getOptionKey(option, index)"
            :modelValue="selectedValues"
            :multiple="multiple"
            :value="option.value"
            :label="option.label"
            :hint="option.hint"
            :disabled="option.disabled"
            :tabindex="option === focusTargetOption ? 0 : undefined"
            :data-select-focus-target="option === focusTargetOption ? 'true' : undefined"
            @click="handleOptionClick(option)"
          )
            slot(name="item" :value="slotValue(option)" :label="option.label" :item="slotItem(option)")
          slot(name="after" :close="closeDropdown")
      template(v-if="multiple" #actions)
        Button.flex-1.min-w-0(label="general.cancel" :variant="ButtonVariant.Secondary" :size="Sizes.HanSolo" @click="closeDropdown")
        Button.flex-1.min-w-0(label="general.apply" :variant="ButtonVariant.Primary" :size="Sizes.HanSolo" @click="applySelection")
</template>

<style scoped src="../../../styles/css/input-sizes.css"></style>

<style scoped>
.placeholder {
  color: var(--cue-content-neutral-tertiary);
  opacity: 0.54;
}
</style>
